import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import ConnectTree from "./ConnectTree";
import { ArrowLeft } from "lucide-react";
import profilePhoto from "@/assets/profile-photo.webp";
import profilePhotoColor from "@/assets/profile-photo-color.webp";

const EDGE_LAYERS = 6;

interface CoinTransitionProps {
  coinStartX: number;
  coinStartY: number;
  cardWidth: number;
  cardHeight: number;
  portraitAspect: number;
  onBack: () => void;
  onPhaseChange?: (phase: 'spin' | 'expand' | 'reveal') => void;
}

const CoinTransition = ({
  coinStartX,
  coinStartY,
  cardWidth,
  cardHeight,
  portraitAspect,
  onBack,
  onPhaseChange,
}: CoinTransitionProps) => {
  const [phase, setPhase] = useState<'spin' | 'expand' | 'reveal'>('spin');

  useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase('expand');
      onPhaseChange?.('expand');
    }, 4200);
    const t2 = setTimeout(() => {
      setPhase('reveal');
      onPhaseChange?.('reveal');
    }, 5800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const padding = 24;

  // Expanded image dimensions (within card padding) - match About page natural image size
  const imgWidth = cardWidth - padding * 2;
  const imgLeft = padding;
  const imgTop = padding + 80;
  const imgHeight = imgWidth * portraitAspect;

  // Coin target: center of where the about page image will be
  const coinTargetX = imgLeft + imgWidth / 2 - 36;
  const coinTargetY = imgTop + imgHeight / 2 - 36;

  const isExpanding = phase === 'expand' || phase === 'reveal';
  const easeSmooth: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

  return (
    <div className="relative z-10 rounded-3xl bg-card">
      {/* Single unified coin/image element — the coin IS the image */}
      <motion.div
        className="absolute overflow-hidden"
        style={{ zIndex: 2, willChange: 'transform, width, height, border-radius' }}
        initial={{
          x: coinStartX,
          y: coinStartY,
          width: 72,
          height: 72,
          borderRadius: 36,
        }}
        animate={{
          x: isExpanding ? imgLeft : coinTargetX,
          y: isExpanding ? imgTop : coinTargetY,
          width: isExpanding ? imgWidth : 72,
          height: isExpanding ? imgHeight : 72,
          borderRadius: isExpanding ? 16 : 36,
        }}
        transition={{
          type: 'tween',
          duration: phase === 'spin' ? 2.5 : 2,
          ease: phase === 'spin' ? easeSmooth : [0.22, 1, 0.36, 1],
        }}
      >
        {/* 3D spinning coin — visible during spin only */}
        <div
          style={{
            perspective: 800,
            position: 'absolute',
            inset: 0,
            opacity: phase === 'spin' ? 1 : 0,
            transition: 'opacity 0.01s',
            pointerEvents: 'none',
          }}
        >
          <div className={`relative h-full w-full coin-3d ${phase === 'spin' ? 'coin-spin-to-back' : ''}`}>
            {/* Front edge layers */}
            {Array.from({ length: EDGE_LAYERS }).map((_, i) => {
              const depth = (i + 1) * 0.8;
              return (
                <div
                  key={i}
                  className="absolute inset-0 rounded-full"
                  style={{
                    transform: `translateZ(${-depth}px)`,
                    background: 'hsl(0, 0%, 75%)',
                    backfaceVisibility: 'hidden',
                  }}
                />
              );
            })}
            {/* Back edge layers */}
            {Array.from({ length: EDGE_LAYERS }).map((_, i) => {
              const depth = (i + 1) * 0.8;
              return (
                <div
                  key={`b-${i}`}
                  className="absolute inset-0 rounded-full"
                  style={{
                    transform: `rotateY(180deg) translateZ(${-depth}px)`,
                    background: 'hsl(0, 0%, 75%)',
                    backfaceVisibility: 'hidden',
                  }}
                />
              );
            })}
            {/* Front face - B&W */}
            <div
              className="absolute inset-0 overflow-hidden rounded-full bg-muted"
              style={{ backfaceVisibility: 'hidden', transform: 'translateZ(1px)' }}
            >
              <img
                src={profilePhoto}
                alt=""
                className="h-full w-full object-cover"
                style={{ transform: 'scale(1.5) translateY(13%)', transformOrigin: 'center center' }}
              />
            </div>
            {/* Back face - Color */}
            <div
              className="absolute inset-0 overflow-hidden rounded-full bg-muted"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg) translateZ(1px)' }}
            >
              <img
                src={profilePhotoColor}
                alt=""
                className="h-full w-full object-cover"
                style={{ transform: 'scale(1.5) translateY(13%)', transformOrigin: 'center center' }}
              />
            </div>
          </div>
        </div>

        {/* Flat color image — hidden behind coin during spin, revealed on expand */}
        <img
          src={profilePhotoColor}
          alt="Jacob Haas"
          className="h-full w-full object-cover"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: phase === 'spin' ? 0 : 1,
            transform: isExpanding ? 'none' : 'scale(1.5) translateY(13%)',
            transformOrigin: 'center center',
          }}
        />
      </motion.div>

      {/* About content - always rendered for layout, fades in on reveal */}
      <div
        className="relative rounded-3xl"
        style={{ opacity: phase === 'reveal' ? 1 : 0, transition: 'opacity 0.35s ease' }}
      >
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div
            className="flex items-center justify-between mb-6"
            style={{ opacity: phase === 'reveal' ? 1 : 0, transform: phase === 'reveal' ? 'translateY(0)' : 'translateY(6px)', transition: 'opacity 0.3s ease, transform 0.3s ease' }}
          >
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">About Me</h1>
              <p className="mt-0.5 text-base text-muted-foreground">Hi, I am Jacob</p>
            </div>
            <button
              onClick={onBack}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </div>

          {/* Spacer matching the image height so text flows below it */}
          <div style={{ height: imgHeight + 16, borderRadius: 16 }} />

          {/* Text content */}
          <div
            className="mt-6 space-y-4"
            style={{ opacity: phase === 'reveal' ? 1 : 0, transform: phase === 'reveal' ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.35s ease 0.08s, transform 0.35s ease 0.08s' }}
          >
            <p className="text-sm font-semibold text-foreground">
              Driven by curiosity and a love for building things.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              I'm a business student at Maastricht University majoring in Entrepreneurship, currently on exchange at Singapore Management University. My journey has taken me through product, design, and growth roles, from building landing pages and optimizing conversion at Aware Health, to supporting SEO strategies at Enpal, to designing web apps at Skip App.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              I'm passionate about the intersection of design, strategy and AI. I love working with tools like Figma and Lovable to turn ideas into real products and thrive when I'm creating something meaningful.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Outside of work, you'll find me travelling, playing hockey, surfing, kiting, cooking or just meeting new people. Always moving, always learning.
            </p>
            <ConnectTree />
            <p className="text-sm text-muted-foreground">Jacob</p>
          </div>

          {/* Back button */}
          <div
            className="mt-8 flex justify-center"
            style={{ opacity: phase === 'reveal' ? 1 : 0, transition: 'opacity 0.3s ease 0.15s' }}
          >
            <button
              onClick={onBack}
              className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              Back Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinTransition;
